// =====================================================================
// ADMIN · CRM — prospectos, cuentas, oportunidades, desempeño y comisiones.
// Regla NULED: solo se vende a cuentas. Toda cuenta pasó por un vendedor y
// su dueño (customers.assigned_to) es quien la abrió. Solo admin reasigna.
// El vendedor ve solo lo suyo; el admin ve todo y puede filtrar por vendedor.
// USD y MXN nunca se suman. Comisión: sobre venta sin IVA, al cobrar,
// por escalones de meta, en MXN al tipo de cambio del día del cobro.
// =====================================================================
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../lib/auth';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type View = 'dia' | 'prospectos' | 'cuentas' | 'oportunidades' | 'desempeno' | 'config';
type StaffOpt = { user_id: string; display_name: string; role: string };
type Open = { kind: 'lead' | 'account' | 'deal'; id: string } | null;

const n = (x: any) => Number(x ?? 0);
const fmtNum = (x: any, d?: number) => n(x).toLocaleString('es-MX', { minimumFractionDigits: d ?? 0, maximumFractionDigits: d ?? 0 });
const fmtMoney = (x: any, ccy: string) => `$${fmtNum(x, 0)} ${ccy}`;
const fmtDate = (d: string | null | undefined) => d ? new Date(d.length === 10 ? d + 'T00:00:00' : d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d: string | null | undefined) => d ? new Date(d).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const todayIso = () => new Date().toISOString().slice(0, 10);
const toLocalInput = (d: string | null | undefined) => {
  if (!d) return '';
  const x = new Date(d); const off = x.getTimezoneOffset();
  return new Date(x.getTime() - off * 60000).toISOString().slice(0, 16);
};

const SOURCE: Record<string, string> = { rep: 'Vendedor', whatsapp: 'WhatsApp', web: 'Página', referral: 'Referido', other: 'Otro' };
const LEAD_STAGES: { code: string; label: string; color: string }[] = [
  { code: 'new', label: 'Nuevo', color: '#B45309' },
  { code: 'contacted', label: 'Contactado', color: '#1D4ED8' },
  { code: 'qualified', label: 'Calificado', color: '#6B21A8' },
  { code: 'converted', label: 'Cuenta abierta', color: '#15803D' },
  { code: 'discarded', label: 'Descartado', color: '#64748B' },
];
const CUSTOMER_TYPES: Record<string, string> = {
  distributor: 'Distribuidor', electrician: 'Electricista', integrator: 'Integrador', project: 'Proyecto / contratista',
  specifier: 'Especificador (arquitecto/diseñador)', end_customer: 'Cliente final',
};
const LEAD_TYPE_TO_CUSTOMER: Record<string, string> = {
  distribuidor: 'distributor', electricista: 'electrician', integrador: 'integrator', arquitecto: 'specifier',
  disenador: 'specifier', contratista: 'project', tienda_online: 'distributor', otro: 'distributor',
};
const HEALTH: Record<string, { label: string; bg: string; fg: string }> = {
  activa: { label: 'Activa', bg: '#DCFCE7', fg: '#15803D' },
  por_activar: { label: 'Por activar', bg: '#FEF9C3', fg: '#854D0E' },
  en_riesgo: { label: 'En riesgo', bg: '#FEE2E2', fg: '#B91C1C' },
  inactiva: { label: 'Inactiva', bg: '#F1F5F9', fg: '#334155' },
};
const ACT_KIND: Record<string, string> = {
  note: 'Nota', call: 'Llamada', meeting: 'Reunión', whatsapp: 'WhatsApp', email: 'Correo', visit: 'Visita', next_step: 'Siguiente paso', system: 'Sistema',
};
const TL_SRC: Record<string, string> = { quote: 'Cotización', order: 'Pedido', payment: 'Cobro', delivery: 'Entrega', portal: 'Portal' };

export function AdminCRM() {
  const { staff } = useAuth();
  const isAdmin = staff?.role === 'admin';
  const [view, setView] = useState<View>('dia');
  const [sellers, setSellers] = useState<StaffOpt[]>([]);
  const [owner, setOwner] = useState<string>('');
  const [open, setOpen] = useState<Open>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    supabase.from('staff').select('user_id, display_name, role').eq('active', true).order('display_name')
      .then(({ data }) => setSellers(((data as any[]) ?? []).filter(s => s.role === 'admin' || s.role === 'sales')));
  }, []);
  useEffect(() => { if (staff && !isAdmin) setOwner(staff.user_id); }, [staff, isAdmin]);

  const ownerArg = isAdmin ? (owner || null) : (staff?.user_id ?? null);
  const refresh = () => setTick(t => t + 1);

  const views: { k: View; label: string }[] = [
    { k: 'dia', label: 'Mi día' }, { k: 'prospectos', label: 'Prospectos' }, { k: 'cuentas', label: 'Cuentas' },
    { k: 'oportunidades', label: 'Oportunidades' }, { k: 'desempeno', label: 'Desempeño y comisiones' },
  ];
  if (isAdmin) views.push({ k: 'config', label: 'Configuración' });

  return (
    <AdminContainer title="CRM" eyebrow="Comercial · prospectos, cuentas y desempeño" action={isAdmin ? (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: theme.textMuted }}>
        Vendedor
        <select value={owner} onChange={e => setOwner(e.target.value)} style={{ ...inp, width: 220 }}>
          <option value="">Todos</option>
          {sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.display_name}</option>)}
        </select>
      </label>
    ) : undefined}>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 22, borderBottom: `1px solid ${theme.border}` }}>
        {views.map(v => (
          <button key={v.k} onClick={() => setView(v.k)} style={{
            padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: theme.font.sans,
            fontSize: 13, fontWeight: view === v.k ? 600 : 400, color: view === v.k ? theme.brand : theme.textMuted,
            borderBottom: view === v.k ? `2px solid ${theme.brand}` : '2px solid transparent', marginBottom: -1,
          }}>{v.label}</button>
        ))}
      </div>

      {view === 'dia' && <MiDia owner={ownerArg} tick={tick} onOpen={setOpen} />}
      {view === 'prospectos' && <Prospectos owner={ownerArg} tick={tick} isAdmin={isAdmin} sellers={sellers} meId={staff?.user_id ?? null} onOpen={setOpen} />}
      {view === 'cuentas' && <Cuentas owner={ownerArg} tick={tick} isAdmin={isAdmin} sellers={sellers} onOpen={setOpen} onChanged={refresh} />}
      {view === 'oportunidades' && <Oportunidades owner={ownerArg} tick={tick} meId={staff?.user_id ?? null} isAdmin={isAdmin} sellers={sellers} onOpen={setOpen} />}
      {view === 'desempeno' && <Desempeno owner={ownerArg} isAdmin={isAdmin} meId={staff?.user_id ?? null} sellers={sellers} />}
      {view === 'config' && isAdmin && <Configuracion sellers={sellers} />}

      {open?.kind === 'lead' && <LeadModal id={open.id} isAdmin={isAdmin} sellers={sellers} onClose={() => setOpen(null)} onChanged={refresh}
        onOpenAccount={(cid) => setOpen({ kind: 'account', id: cid })} />}
      {open?.kind === 'account' && <AccountModal id={open.id} isAdmin={isAdmin} onClose={() => setOpen(null)} onChanged={refresh}
        onOpenDeal={(did) => setOpen({ kind: 'deal', id: did })} />}
      {open?.kind === 'deal' && <DealModal id={open.id} isAdmin={isAdmin} sellers={sellers} onClose={() => setOpen(null)} onChanged={refresh}
        onOpenAccount={(cid) => setOpen({ kind: 'account', id: cid })} />}
    </AdminContainer>
  );
}

// =====================================================================
// MI DÍA
// =====================================================================
function MiDia({ owner, tick, onOpen }: { owner: string | null; tick: number; onOpen: (o: Open) => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setLoading(true); setErr(null);
    supabase.rpc('crm_my_day', { p_staff: owner }).then(({ data, error }) => {
      if (error) setErr(error.message); else setRows((data as any[]) ?? []);
      setLoading(false);
    });
  }, [owner, tick]);
  const groups = [
    { prio: 1, label: 'Urgente', color: RED },
    { prio: 2, label: 'Hoy', color: AMBER },
    { prio: 3, label: 'Pendientes', color: theme.textDim },
  ];
  if (loading) return <Loading />;
  if (err) return <ErrBox>{err}</ErrBox>;
  if (rows.length === 0) return <Empty>Todo al día. No hay señales pendientes. 🎉</Empty>;
  return (
    <div style={{ display: 'grid', gap: 28, maxWidth: 980 }}>
      {groups.map(g => {
        const list = rows.filter(r => n(r.prio) === g.prio);
        if (list.length === 0) return null;
        return (
          <div key={g.prio}>
            <GroupTitle color={g.color} count={list.length}>{g.label}</GroupTitle>
            <div style={{ display: 'grid', gap: 12 }}>
              {Array.from(new Set(list.map(r => r.title as string))).map(title => {
                const items = list.filter(r => r.title === title);
                return (
                  <Card key={title}>
                    <div style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, color: theme.brand, borderBottom: `1px solid ${theme.border}` }}>
                      {title} <span style={{ color: theme.textDim, fontWeight: 400 }}>{items.length}</span>
                    </div>
                    {items.map((r, i) => (
                      <button key={i} onClick={() => onOpen({ kind: r.entity === 'lead' ? 'lead' : r.entity === 'deal' ? 'deal' : 'account', id: r.entity_id })}
                        style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 16, alignItems: 'baseline', padding: '10px 16px', background: 'transparent', border: 'none', borderTop: i ? `1px solid ${theme.border}` : 'none', cursor: 'pointer', fontFamily: theme.font.sans }}>
                        <span style={{ flex: 1, fontSize: 13, color: theme.text }}>{r.detail}</span>
                        <span style={{ fontSize: 12, color: theme.textDim, whiteSpace: 'nowrap' }}>{fmtDate(r.at)}</span>
                      </button>
                    ))}
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =====================================================================
// PROSPECTOS
// =====================================================================
function Prospectos({ owner, tick, isAdmin, sellers, meId, onOpen }: {
  owner: string | null; tick: number; isAdmin: boolean; sellers: StaffOpt[]; meId: string | null; onOpen: (o: Open) => void;
}) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<string>('open');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [localTick, setLocalTick] = useState(0);
  useEffect(() => {
    setLoading(true);
    supabase.rpc('crm_leads_list', { p_owner: owner }).then(({ data, error }) => {
      if (error) alert(error.message); setRows((data as any[]) ?? []); setLoading(false);
    });
  }, [owner, tick, localTick]);
  const count = (s: string) => rows.filter(r => s === 'open' ? !['converted', 'discarded'].includes(r.stage) : r.stage === s).length;
  const list = rows.filter(r => (stage === 'open' ? !['converted', 'discarded'].includes(r.stage) : r.stage === stage))
    .filter(r => !q.trim() || `${r.business_name} ${r.contact_name} ${r.phone} ${r.email} ${r.city}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <Toolbar>
        <Pills>
          <Kpi label="Abiertos" value={count('open')} color={theme.brand} active={stage === 'open'} onClick={() => setStage('open')} />
          {LEAD_STAGES.map(s => <Kpi key={s.code} label={s.label} value={count(s.code)} color={s.color} active={stage === s.code} onClick={() => setStage(s.code)} />)}
        </Pills>
        <input placeholder="Buscar nombre, contacto, teléfono, ciudad…" value={q} onChange={e => setQ(e.target.value)} style={{ ...inp, width: 280 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}>Nuevo prospecto</button>
      </Toolbar>
      {loading ? <Loading /> : (
        <TableBox>
          <thead><tr style={{ background: theme.bgSoft }}>
            <Th>Prospecto</Th><Th>Origen</Th><Th>Tipo</Th><Th>Etapa</Th><Th>Vendedor</Th><Th>Siguiente paso</Th><Th>Llegó</Th>
          </tr></thead>
          <tbody>
            {list.map(r => (
              <tr key={r.id} onClick={() => onOpen({ kind: 'lead', id: r.id })} style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}>
                <Td><Name>{r.business_name}</Name><Sub>{[r.contact_name, r.phone, r.city].filter(Boolean).join(' · ')}</Sub></Td>
                <Td style={{ color: theme.textMuted }}>{SOURCE[r.source] ?? r.source}</Td>
                <Td style={{ color: theme.textMuted }}>{r.customer_type ?? '—'}</Td>
                <Td><StageChip code={r.stage} />{r.sla_breached && <Sub color={RED}>{r.hours_open} h sin contactar</Sub>}</Td>
                <Td>{r.owner_name ?? <span style={{ color: AMBER }}>Sin asignar</span>}</Td>
                <Td><NextStep text={r.next_action} at={r.next_action_at} /></Td>
                <Td style={{ color: theme.textMuted, whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</Td>
              </tr>
            ))}
          </tbody>
          {list.length === 0 && <tbody><tr><td colSpan={7}><Empty>Sin prospectos en esta etapa.</Empty></td></tr></tbody>}
        </TableBox>
      )}
      {creating && <NewLeadModal isAdmin={isAdmin} sellers={sellers} meId={meId} onClose={() => setCreating(false)}
        onSaved={(id) => { setCreating(false); setLocalTick(t => t + 1); onOpen({ kind: 'lead', id }); }} />}
    </div>
  );
}

function NewLeadModal({ isAdmin, sellers, meId, onClose, onSaved }: { isAdmin: boolean; sellers: StaffOpt[]; meId: string | null; onClose: () => void; onSaved: (id: string) => void }) {
  const [f, setF] = useState({ business_name: '', contact_name: '', phone: '', email: '', city: '', state: '', customer_type: 'distribuidor', source: 'rep', interest: '', notes: '', assigned_to: meId ?? '', next_action: '', next_action_at: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF(x => ({ ...x, [k]: v }));
  async function save() {
    if (!f.business_name.trim()) { setErr('Captura el nombre del negocio o de la persona.'); return; }
    setBusy(true); setErr(null);
    const { data, error } = await supabase.from('crm_leads').insert({
      business_name: f.business_name.trim(), contact_name: f.contact_name || null, phone: f.phone || null, whatsapp: f.phone || null,
      email: f.email || null, city: f.city || null, state: f.state || null, customer_type: f.customer_type, source: f.source,
      interest: f.interest || null, notes: f.notes || null, assigned_to: f.assigned_to || meId,
      next_action: f.next_action || null, next_action_at: f.next_action_at ? new Date(f.next_action_at).toISOString() : null,
    }).select('id').single();
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved((data as any).id);
  }
  return (
    <Modal onClose={onClose}>
      <ModalTitle eyebrow="Nuevo prospecto" title="Alta de prospecto" />
      <Grid2>
        <Field label="Negocio / nombre *"><input value={f.business_name} onChange={e => set('business_name', e.target.value)} style={inp} autoFocus /></Field>
        <Field label="Contacto"><input value={f.contact_name} onChange={e => set('contact_name', e.target.value)} style={inp} /></Field>
        <Field label="Teléfono / WhatsApp"><input value={f.phone} onChange={e => set('phone', e.target.value)} style={inp} /></Field>
        <Field label="Email"><input value={f.email} onChange={e => set('email', e.target.value)} style={inp} /></Field>
        <Field label="Ciudad"><input value={f.city} onChange={e => set('city', e.target.value)} style={inp} /></Field>
        <Field label="Estado"><input value={f.state} onChange={e => set('state', e.target.value)} style={inp} /></Field>
        <Field label="Tipo">
          <select value={f.customer_type} onChange={e => set('customer_type', e.target.value)} style={inp}>
            {Object.keys(LEAD_TYPE_TO_CUSTOMER).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Origen">
          <select value={f.source} onChange={e => set('source', e.target.value)} style={inp}>
            {Object.keys(SOURCE).map(k => <option key={k} value={k}>{SOURCE[k]}</option>)}
          </select>
        </Field>
        {isAdmin && (
          <Field label="Vendedor">
            <select value={f.assigned_to} onChange={e => set('assigned_to', e.target.value)} style={inp}>
              {sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.display_name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Qué le interesa"><input value={f.interest} onChange={e => set('interest', e.target.value)} style={inp} placeholder="Riel magnético, Lutron, downlights…" /></Field>
        <Field label="Siguiente paso"><input value={f.next_action} onChange={e => set('next_action', e.target.value)} style={inp} placeholder="Llamar para presentar catálogo" /></Field>
        <Field label="Fecha del siguiente paso"><input type="datetime-local" value={f.next_action_at} onChange={e => set('next_action_at', e.target.value)} style={inp} /></Field>
      </Grid2>
      <div style={{ height: 10 }} />
      <Field label="Notas"><textarea value={f.notes} onChange={e => set('notes', e.target.value)} style={{ ...inp, minHeight: 60 }} /></Field>
      {err && <ErrBox>{err}</ErrBox>}
      <Actions><button onClick={onClose} style={btnGhost}>Cancelar</button><button onClick={save} disabled={busy} style={btnPrimary}>{busy ? 'Guardando…' : 'Guardar prospecto'}</button></Actions>
    </Modal>
  );
}

function LeadModal({ id, isAdmin, sellers, onClose, onChanged, onOpenAccount }: {
  id: string; isAdmin: boolean; sellers: StaffOpt[]; onClose: () => void; onChanged: () => void; onOpenAccount: (cid: string) => void;
}) {
  const [l, setL] = useState<any>(null);
  const [acts, setActs] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [step, setStep] = useState({ text: '', at: '' });
  async function load() {
    const [{ data, error }, { data: a }] = await Promise.all([
      supabase.from('crm_leads').select('*').eq('id', id).maybeSingle(),
      supabase.from('crm_activities').select('id, kind, body, due_at, created_at, created_by').eq('lead_id', id).order('created_at', { ascending: false }),
    ]);
    if (error) setErr(error.message);
    setL(data); setActs((a as any[]) ?? []);
    setStep({ text: (data as any)?.next_action ?? '', at: toLocalInput((data as any)?.next_action_at) });
  }
  useEffect(() => { load(); }, [id]);
  async function update(patch: Record<string, unknown>, log?: string) {
    setErr(null);
    const { error } = await supabase.from('crm_leads').update(patch).eq('id', id);
    if (error) { setErr(error.message); return; }
    if (log) await supabase.from('crm_activities').insert({ lead_id: id, kind: 'system', body: log });
    await load(); onChanged();
  }
  async function setStage(code: string) {
    if (code === 'discarded') {
      const reason = prompt('Motivo para descartar:', '');
      if (reason === null) return;
      await update({ stage: 'discarded', discard_reason: reason || null }, `Descartado: ${reason || 'sin motivo'}`);
      return;
    }
    const label = LEAD_STAGES.find(s => s.code === code)?.label ?? code;
    await update({ stage: code }, `Etapa → ${label}`);
  }
  async function saveStep() {
    await update({ next_action: step.text || null, next_action_at: step.at ? new Date(step.at).toISOString() : null },
      step.text ? `Siguiente paso: ${step.text}${step.at ? ' (' + fmtDateTime(new Date(step.at).toISOString()) + ')' : ''}` : 'Siguiente paso borrado');
  }
  if (!l) return <Modal onClose={onClose}>{err ? <ErrBox>{err}</ErrBox> : <Loading />}</Modal>;
  const closed = l.stage === 'converted' || l.stage === 'discarded';
  return (
    <Modal onClose={onClose} wide>
      <ModalTitle eyebrow={`Prospecto · ${SOURCE[l.source] ?? l.source}`} title={l.business_name} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 24 }}>
        <Pills>
          {LEAD_STAGES.filter(s => s.code !== 'converted').map(s => (
            <button key={s.code} disabled={closed && s.code !== l.stage} onClick={() => setStage(s.code)} style={{
              ...pill(l.stage === s.code), opacity: closed && s.code !== l.stage ? 0.4 : 1,
            }}>{s.label}</button>
          ))}
        </Pills>
        {l.stage !== 'converted' && l.stage !== 'discarded' && <button onClick={() => setConverting(true)} style={{ ...btnPrimary, marginLeft: 'auto' }}>Abrir cuenta</button>}
        {l.stage === 'converted' && l.customer_id && <button onClick={() => onOpenAccount(l.customer_id)} style={{ ...btnPrimary, marginLeft: 'auto' }}>Ver cuenta</button>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 40 }}>
        <div>
          <Section title="Datos">
            <Info k="Contacto" v={l.contact_name} />
            <Info k="Teléfono" v={l.phone} />
            <Info k="Email" v={l.email} />
            <Info k="Ciudad" v={[l.city, l.state].filter(Boolean).join(', ')} />
            <Info k="Tipo" v={l.customer_type} />
            <Info k="Interés" v={l.interest} />
            <Info k="Notas" v={l.notes} />
            <Info k="Llegó" v={fmtDateTime(l.created_at)} />
            {l.contacted_at && <Info k="Primer contacto" v={`${fmtDateTime(l.contacted_at)} (${Math.round((new Date(l.contacted_at).getTime() - new Date(l.created_at).getTime()) / 3600000)} h)`} />}
            {l.discard_reason && <Info k="Motivo descarte" v={l.discard_reason} />}
          </Section>
          {isAdmin && (
            <Section title="Vendedor">
              <select value={l.assigned_to ?? ''} onChange={e => update({ assigned_to: e.target.value || null }, `Asignado a ${sellers.find(s => s.user_id === e.target.value)?.display_name ?? 'nadie'}`)} style={inp}>
                <option value="">Sin asignar</option>
                {sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.display_name}</option>)}
              </select>
            </Section>
          )}
          {!closed && (
            <Section title="Siguiente paso">
              <input value={step.text} onChange={e => setStep(s => ({ ...s, text: e.target.value }))} style={inp} placeholder="¿Qué sigue?" />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input type="datetime-local" value={step.at} onChange={e => setStep(s => ({ ...s, at: e.target.value }))} style={inp} />
                <button onClick={saveStep} style={btnPrimary}>Guardar</button>
              </div>
            </Section>
          )}
        </div>
        <div>
          <Composer onSave={async (kind, body) => { await supabase.from('crm_activities').insert({ lead_id: id, kind, body }); if (l.stage === 'new') await supabase.from('crm_leads').update({ stage: 'contacted' }).eq('id', id); await load(); onChanged(); }} />
          <Section title="Actividad">
            {acts.length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin actividad todavía.</div>}
            {acts.map(a => <ActRow key={a.id} kind={a.kind} body={a.body} at={a.created_at} who={sellers.find(x => x.user_id === a.created_by)?.display_name} />)}
          </Section>
        </div>
      </div>
      {err && <ErrBox>{err}</ErrBox>}
      {converting && <ConvertModal lead={l} onClose={() => setConverting(false)} onDone={(cid) => { setConverting(false); onChanged(); onOpenAccount(cid); }} />}
    </Modal>
  );
}

function ConvertModal({ lead, onClose, onDone }: { lead: any; onClose: () => void; onDone: (cid: string) => void }) {
  const [tiers, setTiers] = useState<any[]>([]);
  const [f, setF] = useState({
    business_name: lead.business_name ?? '', legal_name: '', rfc: '', email: lead.email ?? '', phone: lead.phone ?? '',
    customer_type: LEAD_TYPE_TO_CUSTOMER[lead.customer_type] ?? 'distributor', tier_id: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { supabase.from('customer_tiers').select('id, name').order('name').then(({ data }) => setTiers((data as any[]) ?? [])); }, []);
  const set = (k: string, v: string) => setF(x => ({ ...x, [k]: v }));
  async function go() {
    setBusy(true); setErr(null);
    const { data, error } = await supabase.rpc('crm_convert_lead', {
      p_lead_id: lead.id, p_business_name: f.business_name, p_customer_type: f.customer_type, p_legal_name: f.legal_name || null,
      p_rfc: f.rfc || null, p_email: f.email || null, p_phone: f.phone || null, p_tier_id: f.tier_id || null,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onDone(data as string);
  }
  return (
    <Modal onClose={onClose}>
      <ModalTitle eyebrow="Abrir cuenta" title={lead.business_name} />
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>La cuenta queda a nombre del vendedor del prospecto. Después puedes subir su CSF en Clientes y darle acceso al portal desde su ficha.</div>
      <Grid2>
        <Field label="Nombre comercial"><input value={f.business_name} onChange={e => set('business_name', e.target.value)} style={inp} /></Field>
        <Field label="Tipo de cuenta">
          <select value={f.customer_type} onChange={e => set('customer_type', e.target.value)} style={inp}>
            {Object.keys(CUSTOMER_TYPES).map(k => <option key={k} value={k}>{CUSTOMER_TYPES[k]}</option>)}
          </select>
        </Field>
        <Field label="Razón social"><input value={f.legal_name} onChange={e => set('legal_name', e.target.value)} style={inp} /></Field>
        <Field label="RFC"><input value={f.rfc} onChange={e => set('rfc', e.target.value.toUpperCase())} style={{ ...inp, fontFamily: theme.font.mono }} /></Field>
        <Field label="Email"><input value={f.email} onChange={e => set('email', e.target.value)} style={inp} /></Field>
        <Field label="Teléfono"><input value={f.phone} onChange={e => set('phone', e.target.value)} style={inp} /></Field>
        <Field label="Nivel de precio">
          <select value={f.tier_id} onChange={e => set('tier_id', e.target.value)} style={inp}>
            <option value="">— Asignar después —</option>
            {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
      </Grid2>
      {err && <ErrBox>{err}</ErrBox>}
      <Actions><button onClick={onClose} style={btnGhost}>Cancelar</button><button onClick={go} disabled={busy} style={btnPrimary}>{busy ? 'Abriendo…' : 'Abrir cuenta'}</button></Actions>
    </Modal>
  );
}

// =====================================================================
// CUENTAS
// =====================================================================
function Cuentas({ owner, tick, isAdmin, sellers, onOpen, onChanged }: {
  owner: string | null; tick: number; isAdmin: boolean; sellers: StaffOpt[]; onOpen: (o: Open) => void; onChanged: () => void;
}) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('todas');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [assignTo, setAssignTo] = useState('');
  const [localTick, setLocalTick] = useState(0);
  useEffect(() => {
    setLoading(true); setErr(null);
    supabase.rpc('crm_accounts', { p_owner: owner }).then(({ data, error }) => {
      if (error) setErr(error.message); else setRows((data as any[]) ?? []);
      setLoading(false);
    });
  }, [owner, tick, localTick]);
  const cnt = (h: string) => h === 'todas' ? rows.length : h === 'sin_dueno' ? rows.filter(r => !r.owner_id).length : rows.filter(r => r.health === h).length;
  const list = useMemo(() => rows
    .filter(r => filter === 'todas' ? true : filter === 'sin_dueno' ? !r.owner_id : r.health === filter)
    .filter(r => !q.trim() || `${r.business_name} ${r.legal_name ?? ''} ${r.rfc ?? ''} ${r.owner_name ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => n(b.metrics?.sales_12m_mxn) + n(b.metrics?.sales_12m_usd) * 18 - (n(a.metrics?.sales_12m_mxn) + n(a.metrics?.sales_12m_usd) * 18)),
  [rows, filter, q]);
  const selIds = Object.keys(sel).filter(k => sel[k]);
  async function assign() {
    if (selIds.length === 0) return;
    const who = sellers.find(s => s.user_id === assignTo)?.display_name ?? 'nadie';
    if (!confirm(`Asignar ${selIds.length} cuenta(s) a ${who}?`)) return;
    const { error } = await supabase.rpc('crm_assign_accounts', { p_customer_ids: selIds, p_owner: assignTo || null });
    if (error) { alert(error.message); return; }
    setSel({}); setLocalTick(t => t + 1); onChanged();
  }
  return (
    <div>
      <Toolbar>
        <Pills>
          <Kpi label="Todas" value={cnt('todas')} color={theme.brand} active={filter === 'todas'} onClick={() => setFilter('todas')} />
          {Object.keys(HEALTH).map(h => <Kpi key={h} label={HEALTH[h].label} value={cnt(h)} color={HEALTH[h].fg} active={filter === h} onClick={() => setFilter(h)} />)}
          {isAdmin && <Kpi label="Sin dueño" value={cnt('sin_dueno')} color={AMBER} active={filter === 'sin_dueno'} onClick={() => setFilter('sin_dueno')} />}
        </Pills>
        <input placeholder="Buscar cuenta, razón social, RFC…" value={q} onChange={e => setQ(e.target.value)} style={{ ...inp, width: 280 }} />
      </Toolbar>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {isAdmin && selIds.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: theme.textMuted }}>{selIds.length} seleccionada(s) · asignar a</span>
            <select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={{ ...inp, width: 200 }}>
              <option value="">Sin dueño</option>
              {sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.display_name}</option>)}
            </select>
            <button onClick={assign} style={btnPrimary}>Asignar</button>
          </div>
        )}
      </div>
      {loading ? <Loading /> : err ? <ErrBox>{err}</ErrBox> : (
        <TableBox minWidth={1100}>
          <thead><tr style={{ background: theme.bgSoft }}>
            {isAdmin && <Th><input type="checkbox" checked={list.length > 0 && list.every(r => sel[r.id])} onChange={e => { const v = e.target.checked; const m: Record<string, boolean> = { ...sel }; list.forEach(r => { m[r.id] = v; }); setSel(m); }} /></Th>}
            <Th>Cuenta</Th><Th>Estado</Th><Th>Dueño</Th><Th style={{ textAlign: 'right' }}>Compra 12m</Th><Th>Última compra</Th>
            <Th style={{ textAlign: 'right' }}>Saldo</Th><Th style={{ textAlign: 'right' }}>Cotiz. abiertas</Th><Th>Portal</Th><Th>Siguiente paso</Th>
          </tr></thead>
          <tbody>
            {list.map(r => {
              const m = r.metrics ?? {};
              return (
                <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }} onClick={() => onOpen({ kind: 'account', id: r.id })}>
                  {isAdmin && <Td><input type="checkbox" checked={!!sel[r.id]} onClick={e => e.stopPropagation()} onChange={e => setSel(s => ({ ...s, [r.id]: e.target.checked }))} /></Td>}
                  <Td><Name>{r.business_name}</Name><Sub>{CUSTOMER_TYPES[r.customer_type] ?? r.customer_type}{r.tier ? ` · ${r.tier}` : ''}</Sub></Td>
                  <Td><HealthChip h={r.health} /></Td>
                  <Td>{r.owner_name ?? <span style={{ color: AMBER }}>Sin dueño</span>}</Td>
                  <Td style={{ textAlign: 'right' }}><Ccy mxn={m.sales_12m_mxn} usd={m.sales_12m_usd} /></Td>
                  <Td>{m.last_order_at ? <>{fmtDate(m.last_order_at)}<Sub>hace {m.days_since_last} días{m.avg_interval_days ? ` · cada ~${m.avg_interval_days}` : ''}</Sub></> : <span style={{ color: theme.textDim }}>Nunca</span>}</Td>
                  <Td style={{ textAlign: 'right', color: n(m.balance_mxn) + n(m.balance_usd) > 0 ? AMBER : theme.textDim }}><Ccy mxn={m.balance_mxn} usd={m.balance_usd} /></Td>
                  <Td style={{ textAlign: 'right' }}>{n(m.open_quotes) || <span style={{ color: theme.textDim }}>—</span>}</Td>
                  <Td>{n(m.portal_users) > 0 ? <>{m.portal_users} usuario(s)<Sub>{m.portal_events_30d} visitas 30d</Sub></> : <span style={{ color: AMBER }}>Sin acceso</span>}</Td>
                  <Td><NextStep text={r.next_action} at={r.next_action_at} /></Td>
                </tr>
              );
            })}
          </tbody>
          {list.length === 0 && <tbody><tr><td colSpan={isAdmin ? 10 : 9}><Empty>Sin cuentas con este filtro.</Empty></td></tr></tbody>}
        </TableBox>
      )}
    </div>
  );
}

function AccountModal({ id, isAdmin, onClose, onChanged, onOpenDeal }: {
  id: string; isAdmin: boolean; onClose: () => void; onChanged: () => void; onOpenDeal: (did: string) => void;
}) {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [step, setStep] = useState({ text: '', at: '' });
  const [payOrder, setPayOrder] = useState<any>(null);
  const [addContact, setAddContact] = useState(false);
  const [tlFilter, setTlFilter] = useState('todo');
  async function load() {
    const { data, error } = await supabase.rpc('crm_account', { p_customer_id: id });
    if (error) { setErr(error.message); return; }
    setD(data);
    const c = (data as any)?.customer;
    setStep({ text: c?.next_action ?? '', at: toLocalInput(c?.next_action_at) });
  }
  useEffect(() => { load(); }, [id]);
  async function saveStep() {
    const { error } = await supabase.from('customers').update({ next_action: step.text || null, next_action_at: step.at ? new Date(step.at).toISOString() : null }).eq('id', id);
    if (error) { setErr(error.message); return; }
    if (step.text) await supabase.from('crm_activities').insert({ customer_id: id, kind: 'next_step', body: step.text, due_at: step.at ? new Date(step.at).toISOString() : null });
    await load(); onChanged();
  }
  if (!d) return <Modal onClose={onClose} wide>{err ? <ErrBox>{err}</ErrBox> : <Loading />}</Modal>;
  const c = d.customer; const m = d.metrics ?? {};
  const tl = (d.timeline ?? []).filter((t: any) => tlFilter === 'todo' ? true : tlFilter === 'portal' ? t.src === 'portal' : tlFilter === 'actividad' ? t.src === 'activity' : t.src !== 'portal' && t.src !== 'activity');
  return (
    <Modal onClose={onClose} wide>
      <ModalTitle eyebrow={`Cuenta · ${CUSTOMER_TYPES[c.customer_type] ?? c.customer_type}${c.tier ? ' · ' + c.tier : ''}`} title={c.business_name} />
      <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 22 }}>
        Dueño: <b style={{ color: theme.brand, fontWeight: 600 }}>{c.owner_name ?? 'Sin dueño'}</b> · Cuenta desde {fmtDate(c.account_opened_at)}
        {c.rfc && <> · <span style={{ fontFamily: theme.font.mono }}>{c.rfc}</span></>}
        {c.credit_days ? <> · Crédito {c.credit_days} días</> : null}
      </div>

      <StatStrip>
        <Stat label="Compra 12 meses" value={<Ccy mxn={m.sales_12m_mxn} usd={m.sales_12m_usd} />} />
        <Stat label="Pedidos (12m / total)" value={`${n(m.orders_12m)} / ${n(m.orders_total)}`} />
        <Stat label="Ticket promedio" value={<Ccy mxn={m.avg_ticket_mxn} usd={m.avg_ticket_usd} />} />
        <Stat label="Última compra" value={m.last_order_at ? `hace ${m.days_since_last} días` : 'Nunca'} sub={m.avg_interval_days ? `compra cada ~${m.avg_interval_days} días` : undefined} />
        <Stat label="Saldo por cobrar" value={<Ccy mxn={m.balance_mxn} usd={m.balance_usd} />} warn={n(m.balance_mxn) + n(m.balance_usd) > 0} />
        <Stat label="Portal (30 días)" value={`${n(m.portal_events_30d)} eventos`} sub={`${n(m.portal_users)} usuario(s)`} />
      </StatStrip>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,1fr)', gap: 40, marginTop: 28 }}>
        <div>
          <Section title="Siguiente paso">
            <input value={step.text} onChange={e => setStep(s => ({ ...s, text: e.target.value }))} style={inp} placeholder="¿Qué sigue con esta cuenta?" />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input type="datetime-local" value={step.at} onChange={e => setStep(s => ({ ...s, at: e.target.value }))} style={inp} />
              <button onClick={saveStep} style={btnPrimary}>Guardar</button>
            </div>
          </Section>

          <Composer onSave={async (kind, body) => { await supabase.from('crm_activities').insert({ customer_id: id, kind, body }); await load(); onChanged(); }} />

          <Section title={`Por cobrar (${(d.open_orders ?? []).length})`}>
            {(d.open_orders ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin saldos pendientes.</div>}
            {(d.open_orders ?? []).map((o: any) => (
              <Row key={o.id}>
                <span style={{ fontFamily: theme.font.mono, color: theme.textMuted, fontSize: 12 }}>{o.order_number}</span>
                <span style={{ flex: 1, color: theme.textMuted }}>{fmtDate(o.created_at)} · total {fmtMoney(o.total, o.currency)} · pagado {fmtMoney(o.amount_paid, o.currency)}</span>
                <span style={{ color: AMBER, fontWeight: 600 }}>{fmtMoney(n(o.total) - n(o.amount_paid), o.currency)}</span>
                <button onClick={() => setPayOrder(o)} style={btnLink}>Registrar cobro</button>
              </Row>
            ))}
          </Section>

          <Section title={`Cotizaciones abiertas (${(d.open_quotes ?? []).length})`}>
            {(d.open_quotes ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin cotizaciones abiertas.</div>}
            {(d.open_quotes ?? []).map((q: any) => (
              <Row key={q.id}>
                <span style={{ fontFamily: theme.font.mono, color: theme.textMuted, fontSize: 12 }}>{q.quote_number}</span>
                <span style={{ flex: 1, color: theme.textMuted }}>{q.project_title ? `${q.project_title} · ` : ''}{q.status === 'sent' ? `enviada ${fmtDate(q.sent_at ?? q.created_at)}` : 'borrador'}{q.valid_until ? ` · vence ${fmtDate(q.valid_until)}` : ''}</span>
                <span style={{ fontWeight: 600 }}>{fmtMoney(q.total, q.currency)}</span>
              </Row>
            ))}
          </Section>

          <Section title={`Oportunidades (${(d.deals ?? []).length})`}>
            {(d.deals ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin oportunidades.</div>}
            {(d.deals ?? []).map((x: any) => (
              <button key={x.id} onClick={() => onOpenDeal(x.id)} style={{ display: 'flex', width: '100%', gap: 12, padding: '9px 0', border: 'none', borderTop: `1px solid ${theme.border}`, background: 'transparent', cursor: 'pointer', fontSize: 13, textAlign: 'left', fontFamily: theme.font.sans, color: theme.text }}>
                <span style={{ flex: 1, color: theme.brand, fontWeight: 600 }}>{x.title}</span>
                <span style={{ color: theme.textMuted }}>{x.stage ?? x.status}</span>
                <span style={{ fontWeight: 600 }}>{x.currency === 'USD' ? fmtMoney(x.value_usd, 'USD') : fmtMoney(x.value_mxn, 'MXN')}</span>
              </button>
            ))}
          </Section>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            <Section title="Más visto en el portal (90d)">
              {(d.top_viewed ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin datos todavía.</div>}
              {(d.top_viewed ?? []).map((x: any) => <MiniRow key={x.sku} a={x.name ?? x.sku} b={`${x.n}×`} mono={x.sku} />)}
            </Section>
            <Section title="Búsquedas (90d)">
              {(d.top_searches ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin datos todavía.</div>}
              {(d.top_searches ?? []).map((x: any) => <MiniRow key={x.q} a={`"${x.q}"`} b={`${x.n}×`} />)}
            </Section>
          </div>
          <Section title="Lo que más compra (12m)">
            {(d.top_bought ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin compras en 12 meses.</div>}
            {(d.top_bought ?? []).map((x: any) => <MiniRow key={x.sku} a={x.description ?? x.sku} b={`${fmtNum(x.qty)} pzs · ${x.orders} ped.`} mono={x.sku} />)}
          </Section>
        </div>

        <div>
          <Section title="Contactos" action={<button onClick={() => setAddContact(true)} style={btnLink}>Agregar contacto</button>}>
            {(d.contacts ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin contactos.</div>}
            {(d.contacts ?? []).map((x: any) => (
              <div key={x.id} style={{ fontSize: 13, padding: '9px 0', borderTop: `1px solid ${theme.border}` }}>
                <span style={{ color: theme.brand, fontWeight: 600 }}>{x.full_name}</span>{x.role && <span style={{ color: theme.textMuted }}> · {x.role}</span>}{x.is_primary && <span style={{ color: theme.textMuted }}> · principal</span>}
                <Sub>{[x.phone, x.whatsapp !== x.phone ? x.whatsapp : null, x.email].filter(Boolean).join(' · ')}</Sub>
              </div>
            ))}
          </Section>
          <PortalAccess customerId={id} contacts={d.contacts ?? []} email={c.email} onChanged={load} />
          <Section title="Línea de tiempo">
            <Pills style={{ marginBottom: 6 }}>
              {[['todo', 'Todo'], ['actividad', 'Notas y actividad'], ['ventas', 'Ventas y cobros'], ['portal', 'Portal']].map(([k, label]) => (
                <button key={k} onClick={() => setTlFilter(k)} style={pill(tlFilter === k)}>{label}</button>
              ))}
            </Pills>
            <div style={{ maxHeight: 620, overflowY: 'auto' }}>
              {tl.length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin eventos.</div>}
              {tl.map((t: any, i: number) => <TimelineRow key={i} t={t} />)}
            </div>
          </Section>
        </div>
      </div>
      {err && <ErrBox>{err}</ErrBox>}
      {payOrder && <PaymentModal order={payOrder} isAdmin={isAdmin} onClose={() => setPayOrder(null)} onSaved={() => { setPayOrder(null); load(); onChanged(); }} />}
      {addContact && <ContactModal customerId={id} onClose={() => setAddContact(false)} onSaved={() => { setAddContact(false); load(); }} />}
    </Modal>
  );
}

function TimelineRow({ t }: { t: any }) {
  const type = t.src === 'activity' ? (ACT_KIND[t.kind] ?? t.kind) : (TL_SRC[t.src] ?? t.src);
  const d = t.at ? new Date(t.at) : null;
  return (
    <div style={{ display: 'flex', gap: 14, padding: '9px 0', borderTop: `1px solid ${theme.border}`, fontSize: 13 }}>
      <div style={{ width: 62, flexShrink: 0, color: theme.textDim, fontSize: 12 }}>
        {d ? d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '—'}
        <div style={{ fontSize: 11 }}>{d ? d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 1 }}>{type}{t.who ? ` · ${t.who}` : ''}</div>
        <div style={{ color: theme.text, whiteSpace: 'pre-wrap' }}>{t.title}</div>
        {t.detail && <div style={{ color: theme.textMuted, fontSize: 12 }}>{t.detail}</div>}
      </div>
    </div>
  );
}

function PortalAccess({ customerId, contacts, email, onChanged }: { customerId: string; contacts: any[]; email: string | null; onChanged: () => void }) {
  const [users, setUsers] = useState<any[] | null>(null);
  const [newEmail, setNewEmail] = useState(email ?? (contacts.find(c => c.email)?.email ?? ''));
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function call(body: Record<string, unknown>) {
    setBusy(true); setErr(null);
    const { data, error } = await supabase.functions.invoke('crm-portal-access', { body: { customer_id: customerId, ...body } });
    setBusy(false);
    if (error) {
      let msg = error.message;
      try { const ctx = (error as any).context; if (ctx && typeof ctx.json === 'function') { const j = await ctx.json(); if (j?.error) msg = j.error; } } catch { /* sin detalle */ }
      setErr(msg); return null;
    }
    return data as any;
  }
  async function load() { const r = await call({ action: 'list' }); if (r) setUsers(r.users ?? []); }
  useEffect(() => { load(); }, [customerId]);
  async function grant() {
    const r = await call({ action: 'grant', email: newEmail });
    if (r) { setLink(r.magic_link ?? null); await load(); onChanged(); }
  }
  async function relink(em: string) { const r = await call({ action: 'link', email: em }); if (r) setLink(r.magic_link ?? null); }
  return (
    <Section title="Acceso al portal">
      {users === null && !err && <div style={{ fontSize: 12, color: theme.textDim }}>Cargando…</div>}
      {users && users.length === 0 && <div style={{ fontSize: 13, color: AMBER, marginBottom: 8 }}>Esta cuenta todavía no puede entrar al portal (no ve precios ni deja actividad).</div>}
      {(users ?? []).map(u => (
        <Row key={u.user_id}>
          <span style={{ flex: 1 }}>{u.email} <span style={{ color: theme.textMuted }}>· último acceso {u.last_sign_in_at ? fmtDate(u.last_sign_in_at) : 'nunca'}</span></span>
          <button onClick={() => relink(u.email)} disabled={busy} style={btnLink}>Nueva liga</button>
        </Row>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email del contacto" style={inp} />
        <button onClick={grant} disabled={busy || !newEmail.trim()} style={btnGhost}>{busy ? '…' : 'Dar acceso'}</button>
      </div>
      {link && (
        <div style={{ marginTop: 8, padding: 10, background: theme.bgSoft, fontSize: 12 }}>
          Liga de acceso (mándala por WhatsApp, entra sin contraseña):
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input readOnly value={link} style={{ ...inp, fontFamily: theme.font.mono, fontSize: 11 }} onFocus={e => e.currentTarget.select()} />
            <button onClick={() => { void navigator.clipboard.writeText(link); }} style={btnGhost}>Copiar</button>
          </div>
        </div>
      )}
      {err && <ErrBox>{err}</ErrBox>}
    </Section>
  );
}

function ContactModal({ customerId, onClose, onSaved }: { customerId: string; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ full_name: '', role: '', phone: '', whatsapp: '', email: '', is_primary: false });
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    if (!f.full_name.trim()) { setErr('Captura el nombre.'); return; }
    const { error } = await supabase.from('customer_contacts').insert({
      customer_id: customerId, full_name: f.full_name.trim(), role: f.role || null, phone: f.phone || null,
      whatsapp: f.whatsapp || f.phone || null, email: f.email || null, is_primary: f.is_primary,
    });
    if (error) { setErr(error.message); return; }
    onSaved();
  }
  return (
    <Modal onClose={onClose}>
      <ModalTitle eyebrow="Contacto" title="Nuevo contacto" />
      <Grid2>
        <Field label="Nombre *"><input value={f.full_name} onChange={e => setF(x => ({ ...x, full_name: e.target.value }))} style={inp} autoFocus /></Field>
        <Field label="Puesto"><input value={f.role} onChange={e => setF(x => ({ ...x, role: e.target.value }))} style={inp} placeholder="Compras, dueño, residente…" /></Field>
        <Field label="Teléfono"><input value={f.phone} onChange={e => setF(x => ({ ...x, phone: e.target.value }))} style={inp} /></Field>
        <Field label="WhatsApp"><input value={f.whatsapp} onChange={e => setF(x => ({ ...x, whatsapp: e.target.value }))} style={inp} /></Field>
        <Field label="Email"><input value={f.email} onChange={e => setF(x => ({ ...x, email: e.target.value }))} style={inp} /></Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 18 }}><input type="checkbox" checked={f.is_primary} onChange={e => setF(x => ({ ...x, is_primary: e.target.checked }))} /> Contacto principal</label>
      </Grid2>
      {err && <ErrBox>{err}</ErrBox>}
      <Actions><button onClick={onClose} style={btnGhost}>Cancelar</button><button onClick={save} style={btnPrimary}>Guardar</button></Actions>
    </Modal>
  );
}

function PaymentModal({ order, isAdmin, onClose, onSaved }: { order: any; isAdmin: boolean; onClose: () => void; onSaved: () => void }) {
  const saldo = Math.max(n(order.total) - n(order.amount_paid), 0);
  const [f, setF] = useState({ amount: saldo.toFixed(2), currency: order.currency as string, method: 'transferencia', paid_at: todayIso(), reference: '', notes: '' });
  const [payments, setPayments] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);
  async function loadPayments() {
    const { data } = await supabase.from('order_payments').select('id, amount, currency, method, reference, paid_at, fx_rate, commission_base_mxn').eq('order_id', order.id).order('paid_at', { ascending: false });
    setPayments((data as any[]) ?? []);
  }
  useEffect(() => { loadPayments(); }, [order.id]);
  async function save() {
    if (!(Number(f.amount) > 0)) { setErr('El monto debe ser mayor a 0.'); return; }
    if (!confirmStep) { setConfirmStep(true); return; }
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('crm_register_payment', {
      p_order_id: order.id, p_amount: Number(f.amount), p_currency: f.currency, p_method: f.method, p_paid_at: f.paid_at,
      p_reference: f.reference || null, p_notes: f.notes || null,
    });
    setBusy(false);
    if (error) { setErr(error.message); setConfirmStep(false); return; }
    onSaved();
  }
  async function del(pid: string) {
    if (!confirm('¿Borrar este cobro? Se recalcula el saldo del pedido y la comisión.')) return;
    const { error } = await supabase.rpc('crm_delete_payment', { p_payment_id: pid });
    if (error) { setErr(error.message); return; }
    loadPayments();
  }
  return (
    <Modal onClose={onClose}>
      <ModalTitle eyebrow="Registrar cobro" title={`Pedido ${order.order_number ?? ''}`} />
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>
        Total {fmtMoney(order.total, order.currency)} · pagado {fmtMoney(order.amount_paid, order.currency)} · <b style={{ color: '#B45309' }}>saldo {fmtMoney(saldo, order.currency)}</b>.
        Si el cobro llega en otra moneda, se convierte con el tipo de cambio del día del cobro.
      </div>
      <Grid2>
        <Field label="Monto"><input type="number" step="0.01" value={f.amount} onChange={e => { setF(x => ({ ...x, amount: e.target.value })); setConfirmStep(false); }} style={inp} /></Field>
        <Field label="Moneda del cobro">
          <select value={f.currency} onChange={e => { setF(x => ({ ...x, currency: e.target.value })); setConfirmStep(false); }} style={inp}><option value="MXN">MXN</option><option value="USD">USD</option></select>
        </Field>
        <Field label="Forma de pago">
          <select value={f.method} onChange={e => setF(x => ({ ...x, method: e.target.value }))} style={inp}>
            {['transferencia', 'efectivo', 'tarjeta', 'cheque', 'credito', 'otro'].map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Fecha del cobro"><input type="date" value={f.paid_at} onChange={e => { setF(x => ({ ...x, paid_at: e.target.value })); setConfirmStep(false); }} style={inp} /></Field>
        <Field label="Referencia / folio bancario"><input value={f.reference} onChange={e => setF(x => ({ ...x, reference: e.target.value }))} style={inp} /></Field>
        <Field label="Notas"><input value={f.notes} onChange={e => setF(x => ({ ...x, notes: e.target.value }))} style={inp} /></Field>
      </Grid2>
      {confirmStep && <div style={{ marginTop: 14, padding: '10px 12px', background: theme.bgSoft, borderLeft: `3px solid ${AMBER}`, fontSize: 13, color: theme.text }}>Confirma: cobro de <b>{fmtMoney(f.amount, f.currency)}</b> el {fmtDate(f.paid_at)} por {f.method}. Presiona de nuevo para registrar.</div>}
      {err && <ErrBox>{err}</ErrBox>}
      <Actions><button onClick={onClose} style={btnGhost}>Cancelar</button><button onClick={save} disabled={busy} style={btnPrimary}>{busy ? 'Registrando…' : confirmStep ? 'Confirmar cobro' : 'Registrar cobro'}</button></Actions>
      {payments.length > 0 && (
        <Section title="Cobros de este pedido">
          {payments.map(p => (
            <Row key={p.id}>
              <span style={{ flex: 1, color: theme.textMuted }}>{fmtDate(p.paid_at)} · {p.method}{p.reference ? ` · ${p.reference}` : ''}{p.fx_rate ? ` · TC ${n(p.fx_rate).toFixed(4)}` : ''}</span>
              <span style={{ fontWeight: 600 }}>{fmtMoney(p.amount, p.currency)}</span>
              {isAdmin && <button onClick={() => del(p.id)} style={{ ...btnLink, color: RED }}>Borrar</button>}
            </Row>
          ))}
        </Section>
      )}
    </Modal>
  );
}

// =====================================================================
// OPORTUNIDADES (pipeline)
// =====================================================================
function Oportunidades({ owner, tick, meId, isAdmin, sellers, onOpen }: {
  owner: string | null; tick: number; meId: string | null; isAdmin: boolean; sellers: StaffOpt[]; onOpen: (o: Open) => void;
}) {
  const [channel, setChannel] = useState<'project_spec' | 'whatsapp_inbound'>('project_spec');
  const [stages, setStages] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [localTick, setLocalTick] = useState(0);
  useEffect(() => {
    setLoading(true);
    Promise.all([
      supabase.from('pipeline_stages').select('*').eq('active', true).order('sort_order'),
      supabase.rpc('crm_deals', { p_owner: owner }),
    ]).then(([s, d]) => {
      setStages((s.data as any[]) ?? []);
      if (d.error) alert(d.error.message);
      setDeals((d.data as any[]) ?? []);
      setLoading(false);
    });
  }, [owner, tick, localTick]);
  const chStages = stages.filter(s => s.channel === channel && (showClosed || (!s.is_won && !s.is_lost)));
  const chDeals = deals.filter(d => d.channel === channel);
  const open = chDeals.filter(d => d.status === 'open');
  const sumBy = (ccy: string, weighted: boolean) => open.filter(d => d.currency === ccy).reduce((a, d) => a + (ccy === 'USD' ? n(d.value_usd) : n(d.value_mxn)) * (weighted ? n(d.probability) / 100 : 1), 0);
  return (
    <div>
      <Toolbar>
        <Pills>
          {[['project_spec', 'Proyectos'], ['whatsapp_inbound', 'Recompra']].map(([k, label]) => (
            <button key={k} onClick={() => setChannel(k as any)} style={pill(channel === k)}>{label}</button>
          ))}
        </Pills>
        <span style={{ fontSize: 13, color: theme.textMuted }}>
          {open.length} abiertas · <Ccy mxn={sumBy('MXN', false)} usd={sumBy('USD', false)} inline /> · ponderado <Ccy mxn={sumBy('MXN', true)} usd={sumBy('USD', true)} inline />
        </span>
        <label style={{ fontSize: 13, color: theme.textMuted, display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}><input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} /> Ver ganadas/perdidas</label>
        <button onClick={() => setCreating(true)} style={btnPrimary}>Nueva oportunidad</button>
      </Toolbar>
      {loading ? <Loading /> : (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
          {chStages.map(s => {
            const col = chDeals.filter(d => d.stage_id === s.id && (showClosed || d.status === 'open'));
            return (
              <div key={s.id} style={{ minWidth: 236, flex: '0 0 236px' }}>
                <div style={{ padding: '0 2px 10px' }} title={s.description ?? ''}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.brand, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Dot color={s.ui_color ?? theme.brand} />{s.name} <span style={{ color: theme.textDim, fontWeight: 400 }}>{col.length}</span>
                  </div>
                  <div style={{ fontSize: 11, color: theme.textDim, marginTop: 2, minHeight: 15 }}>{s.description}</div>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {col.map(d => (
                    <button key={d.id} onClick={() => onOpen({ kind: 'deal', id: d.id })} style={{ textAlign: 'left', padding: '12px 14px', border: `1px solid ${theme.border}`, background: theme.bg, cursor: 'pointer', fontFamily: theme.font.sans }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.brand }}>{d.title}</div>
                      <Sub>{d.customer_name ?? 'Sin cuenta'}{isAdmin && d.owner_name ? ` · ${d.owner_name}` : ''}</Sub>
                      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8, color: theme.text }}>{d.currency === 'USD' ? fmtMoney(d.value_usd, 'USD') : fmtMoney(d.value_mxn, 'MXN')} <span style={{ fontWeight: 400, color: theme.textMuted }}>· {n(d.probability)}%</span></div>
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${theme.border}` }}>
                        {d.next_action ? <NextStep text={d.next_action} at={d.next_action_at} />
                          : d.status === 'open' && <span style={{ fontSize: 12, color: AMBER }}>Sin siguiente paso</span>}
                      </div>
                    </button>
                  ))}
                  {col.length === 0 && <div style={{ fontSize: 12, color: theme.textDim, padding: '14px', border: `1px dashed ${theme.border}`, textAlign: 'center' }}>Vacío</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {creating && <NewDealModal channel={channel} stages={stages} owner={owner} meId={meId} isAdmin={isAdmin} sellers={sellers}
        onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); setLocalTick(t => t + 1); onOpen({ kind: 'deal', id }); }} />}
    </div>
  );
}

function NewDealModal({ channel, stages, owner, meId, isAdmin, sellers, onClose, onSaved }: {
  channel: string; stages: any[]; owner: string | null; meId: string | null; isAdmin: boolean; sellers: StaffOpt[]; onClose: () => void; onSaved: (id: string) => void;
}) {
  const [accounts, setAccounts] = useState<any[]>([]);
  const first = stages.filter(s => s.channel === channel && !s.is_won && !s.is_lost)[0];
  const [f, setF] = useState({
    title: '', customer_id: '', currency: 'MXN', value: '', expected_close: '', assigned_to: owner ?? meId ?? '',
    project_address: '', project_city: '', project_type: '', project_specifier_name: '', project_specifier_firm: '', project_estimated_install_date: '',
    next_action: '', next_action_at: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { supabase.rpc('crm_accounts', { p_owner: owner }).then(({ data }) => setAccounts(((data as any[]) ?? []).map(a => ({ id: a.id, name: a.business_name, owner_id: a.owner_id })))); }, [owner]);
  const set = (k: string, v: string) => setF(x => ({ ...x, [k]: v }));
  const isProject = channel === 'project_spec';
  async function save() {
    if (!f.title.trim()) { setErr('Ponle nombre a la oportunidad (obra o pedido).'); return; }
    if (!first) { setErr('No hay etapas configuradas para este pipeline.'); return; }
    setBusy(true); setErr(null);
    const acc = accounts.find(a => a.id === f.customer_id);
    const val = Number(f.value) || 0;
    const { data, error } = await supabase.from('deals').insert({
      channel, stage_id: first.id, status: 'open', title: f.title.trim(), customer_id: f.customer_id || null,
      assigned_to: f.assigned_to || acc?.owner_id || meId, currency: f.currency,
      value_mxn: f.currency === 'MXN' ? val : null, value_usd: f.currency === 'USD' ? val : null,
      probability: first.default_probability ?? 10, expected_close: f.expected_close || null, source: 'crm', created_by: meId,
      project_address: isProject ? f.project_address || null : null, project_city: isProject ? f.project_city || null : null,
      project_type: isProject ? f.project_type || null : null, project_specifier_name: isProject ? f.project_specifier_name || null : null,
      project_specifier_firm: isProject ? f.project_specifier_firm || null : null,
      project_estimated_install_date: isProject ? f.project_estimated_install_date || null : null,
      next_action: f.next_action || null, next_action_at: f.next_action_at ? new Date(f.next_action_at).toISOString() : null,
    }).select('id').single();
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved((data as any).id);
  }
  return (
    <Modal onClose={onClose} wide>
      <ModalTitle eyebrow={isProject ? 'Nuevo proyecto' : 'Nueva recompra'} title="Nueva oportunidad" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12 }}>
        <Field label={isProject ? 'Obra / proyecto *' : 'Descripción *'}><input value={f.title} onChange={e => set('title', e.target.value)} style={inp} autoFocus /></Field>
        <Field label="Cuenta que compra">
          <select value={f.customer_id} onChange={e => set('customer_id', e.target.value)} style={inp}>
            <option value="">— Todavía no definida —</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        {isAdmin && (
          <Field label="Vendedor">
            <select value={f.assigned_to} onChange={e => set('assigned_to', e.target.value)} style={inp}>
              {sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.display_name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Moneda"><select value={f.currency} onChange={e => set('currency', e.target.value)} style={inp}><option value="MXN">MXN</option><option value="USD">USD</option></select></Field>
        <Field label="Monto estimado (sin IVA)"><input type="number" value={f.value} onChange={e => set('value', e.target.value)} style={inp} /></Field>
        <Field label="Fecha probable de compra"><input type="date" value={f.expected_close} onChange={e => set('expected_close', e.target.value)} style={inp} /></Field>
        {isProject && <>
          <Field label="Despacho que especifica"><input value={f.project_specifier_firm} onChange={e => set('project_specifier_firm', e.target.value)} style={inp} /></Field>
          <Field label="Arquitecto / diseñador"><input value={f.project_specifier_name} onChange={e => set('project_specifier_name', e.target.value)} style={inp} /></Field>
          <Field label="Tipo de proyecto"><input value={f.project_type} onChange={e => set('project_type', e.target.value)} style={inp} placeholder="Residencial, hotel, oficinas…" /></Field>
          <Field label="Ubicación de la obra"><input value={f.project_address} onChange={e => set('project_address', e.target.value)} style={inp} /></Field>
          <Field label="Ciudad"><input value={f.project_city} onChange={e => set('project_city', e.target.value)} style={inp} /></Field>
          <Field label="Instalación estimada"><input type="date" value={f.project_estimated_install_date} onChange={e => set('project_estimated_install_date', e.target.value)} style={inp} /></Field>
        </>}
        <Field label="Siguiente paso"><input value={f.next_action} onChange={e => set('next_action', e.target.value)} style={inp} /></Field>
        <Field label="Fecha del siguiente paso"><input type="datetime-local" value={f.next_action_at} onChange={e => set('next_action_at', e.target.value)} style={inp} /></Field>
      </div>
      {err && <ErrBox>{err}</ErrBox>}
      <Actions><button onClick={onClose} style={btnGhost}>Cancelar</button><button onClick={save} disabled={busy} style={btnPrimary}>{busy ? 'Guardando…' : 'Crear oportunidad'}</button></Actions>
    </Modal>
  );
}

function DealModal({ id, isAdmin, sellers, onClose, onChanged, onOpenAccount }: {
  id: string; isAdmin: boolean; sellers: StaffOpt[]; onClose: () => void; onChanged: () => void; onOpenAccount: (cid: string) => void;
}) {
  const [d, setD] = useState<any>(null);
  const [stages, setStages] = useState<any[]>([]);
  const [acts, setActs] = useState<any[]>([]);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [edit, setEdit] = useState({ value: '', probability: '', expected_close: '', next_action: '', next_action_at: '' });
  const [err, setErr] = useState<string | null>(null);
  async function load() {
    const { data, error } = await supabase.from('deals').select('*').eq('id', id).maybeSingle();
    if (error || !data) { setErr(error?.message ?? 'Oportunidad no encontrada'); return; }
    const x = data as any;
    setD(x);
    setEdit({ value: String((x.currency === 'USD' ? x.value_usd : x.value_mxn) ?? ''), probability: String(x.probability ?? ''), expected_close: x.expected_close ?? '', next_action: x.next_action ?? '', next_action_at: toLocalInput(x.next_action_at) });
    const [s, a, q, c] = await Promise.all([
      supabase.from('pipeline_stages').select('*').eq('channel', x.channel).eq('active', true).order('sort_order'),
      supabase.from('crm_activities').select('id, kind, body, created_at, created_by').eq('deal_id', id).order('created_at', { ascending: false }),
      supabase.from('quotes').select('id, quote_number, version, status, currency, total, created_at').eq('deal_id', id).order('created_at', { ascending: false }),
      x.customer_id ? supabase.from('customers').select('business_name').eq('id', x.customer_id).maybeSingle() : Promise.resolve({ data: null }),
    ]);
    setStages((s.data as any[]) ?? []); setActs((a.data as any[]) ?? []); setQuotes((q.data as any[]) ?? []);
    setCustomerName((c.data as any)?.business_name ?? null);
  }
  useEffect(() => { load(); }, [id]);
  async function update(patch: Record<string, unknown>, log?: string) {
    setErr(null);
    const { error } = await supabase.from('deals').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id);
    if (error) { setErr(error.message); return; }
    if (log) await supabase.from('crm_activities').insert({ deal_id: id, customer_id: d?.customer_id ?? null, kind: 'system', body: log });
    await load(); onChanged();
  }
  async function moveStage(s: any) {
    if (s.is_lost) {
      const reason = prompt('¿Por qué se perdió? (precio, tiempo de entrega, competidor, se canceló la obra…)', '');
      if (reason === null) return;
      const to = prompt('¿Contra quién se perdió? (opcional)', '') ?? '';
      await update({ stage_id: s.id, status: 'lost', lost_reason: reason || null, lost_to: to || null, probability: 0, actual_close: todayIso() }, `Perdida: ${reason}${to ? ' vs ' + to : ''}`);
      return;
    }
    if (s.is_won) {
      if (!confirm('¿Marcar como ganada?')) return;
      await update({ stage_id: s.id, status: 'won', probability: 100, actual_close: todayIso() }, 'Ganada 🎉');
      return;
    }
    await update({ stage_id: s.id, status: 'open', probability: s.default_probability ?? d.probability }, `Etapa → ${s.name}`);
  }
  async function saveEdit() {
    const val = Number(edit.value) || 0;
    await update({
      value_mxn: d.currency === 'MXN' ? val : d.value_mxn, value_usd: d.currency === 'USD' ? val : d.value_usd,
      probability: edit.probability === '' ? null : Math.max(0, Math.min(100, Number(edit.probability))),
      expected_close: edit.expected_close || null, next_action: edit.next_action || null,
      next_action_at: edit.next_action_at ? new Date(edit.next_action_at).toISOString() : null,
    }, edit.next_action && edit.next_action !== d.next_action ? `Siguiente paso: ${edit.next_action}` : undefined);
  }
  if (!d) return <Modal onClose={onClose} wide>{err ? <ErrBox>{err}</ErrBox> : <Loading />}</Modal>;
  return (
    <Modal onClose={onClose} wide>
      <ModalTitle eyebrow={`${d.channel === 'project_spec' ? 'Proyecto' : 'Recompra'} · ${d.deal_number ?? ''} · ${d.status === 'open' ? 'abierta' : d.status === 'won' ? 'ganada' : 'perdida'}`} title={d.title} />
      <Pills style={{ marginBottom: 24 }}>
        {stages.map(s => (
          <button key={s.id} onClick={() => moveStage(s)} style={pill(d.stage_id === s.id)}>{s.name}</button>
        ))}
      </Pills>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 40 }}>
        <div>
          <Section title="Datos">
            <Info k="Cuenta" v={customerName ? <a onClick={() => onOpenAccount(d.customer_id)} style={{ color: theme.brand, textDecoration: 'underline', cursor: 'pointer' }}>{customerName}</a> : 'Sin cuenta'} />
            {isAdmin ? (
              <Info k="Vendedor" v={
                <select value={d.assigned_to ?? ''} onChange={e => update({ assigned_to: e.target.value || null }, `Asignada a ${sellers.find(s => s.user_id === e.target.value)?.display_name ?? 'nadie'}`)} style={{ ...inp, width: 200 }}>
                  <option value="">—</option>{sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.display_name}</option>)}
                </select>} />
            ) : <Info k="Vendedor" v={sellers.find(s => s.user_id === d.assigned_to)?.display_name ?? '—'} />}
            {d.channel === 'project_spec' && <>
              <Info k="Especifica" v={[d.project_specifier_name, d.project_specifier_firm].filter(Boolean).join(' · ')} />
              <Info k="Tipo" v={d.project_type} />
              <Info k="Obra" v={[d.project_address, d.project_city].filter(Boolean).join(', ')} />
              <Info k="Instalación" v={d.project_estimated_install_date ? fmtDate(d.project_estimated_install_date) : null} />
            </>}
            {d.lost_reason && <Info k="Se perdió por" v={`${d.lost_reason}${d.lost_to ? ' vs ' + d.lost_to : ''}`} />}
          </Section>
          <Section title="Monto, probabilidad y siguiente paso">
            <Grid2>
              <Field label={`Monto sin IVA (${d.currency})`}><input type="number" value={edit.value} onChange={e => setEdit(x => ({ ...x, value: e.target.value }))} style={inp} /></Field>
              <Field label="Probabilidad %"><input type="number" value={edit.probability} onChange={e => setEdit(x => ({ ...x, probability: e.target.value }))} style={inp} /></Field>
              <Field label="Fecha probable"><input type="date" value={edit.expected_close} onChange={e => setEdit(x => ({ ...x, expected_close: e.target.value }))} style={inp} /></Field>
              <Field label="Fecha siguiente paso"><input type="datetime-local" value={edit.next_action_at} onChange={e => setEdit(x => ({ ...x, next_action_at: e.target.value }))} style={inp} /></Field>
            </Grid2>
            <div style={{ height: 8 }} />
            <Field label="Siguiente paso"><input value={edit.next_action} onChange={e => setEdit(x => ({ ...x, next_action: e.target.value }))} style={inp} /></Field>
            <Actions><button onClick={saveEdit} style={btnPrimary}>Guardar</button></Actions>
          </Section>
          <Section title={`Cotizaciones ligadas (${quotes.length})`}>
            {quotes.length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Liga cotizaciones a esta oportunidad desde Cotizaciones (campo oportunidad).</div>}
            {quotes.map(q => <MiniRow key={q.id} a={`${q.quote_number ?? ''} v${q.version} · ${q.status}`} b={fmtMoney(q.total, q.currency)} />)}
          </Section>
        </div>
        <div>
          <Composer onSave={async (kind, body) => { await supabase.from('crm_activities').insert({ deal_id: id, customer_id: d.customer_id ?? null, kind, body }); await load(); onChanged(); }} />
          <Section title="Actividad">
            {acts.length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin actividad todavía.</div>}
            {acts.map(a => <ActRow key={a.id} kind={a.kind} body={a.body} at={a.created_at} who={sellers.find(s => s.user_id === a.created_by)?.display_name} />)}
          </Section>
        </div>
      </div>
      {err && <ErrBox>{err}</ErrBox>}
    </Modal>
  );
}

// =====================================================================
// DESEMPEÑO Y COMISIONES
// =====================================================================
function monthBounds(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const from = `${ym}-01`;
  const last = new Date(y, m, 0).getDate();
  return { from, to: `${ym}-${String(last).padStart(2, '0')}` };
}

function Desempeno({ owner, isAdmin, meId, sellers }: { owner: string | null; isAdmin: boolean; meId: string | null; sellers: StaffOpt[] }) {
  const [ym, setYm] = useState(todayIso().slice(0, 7));
  const [pick, setPick] = useState<string | null>(null);
  const target = owner ?? pick ?? (isAdmin ? null : meId);
  const { from, to } = monthBounds(ym);
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <Field label="Mes"><input type="month" value={ym} onChange={e => setYm(e.target.value)} style={{ ...inp, width: 180 }} /></Field>
        {target && isAdmin && !owner && <button onClick={() => setPick(null)} style={{ ...btnLink, marginTop: 16 }}>← Todo el equipo</button>}
      </div>
      {target ? <SellerPerformance staffId={target} name={sellers.find(s => s.user_id === target)?.display_name ?? ''} from={from} to={to} />
        : <TeamTable from={from} to={to} onPick={setPick} />}
    </div>
  );
}

function TeamTable({ from, to, onPick }: { from: string; to: string; onPick: (id: string) => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setLoading(true); setErr(null);
    supabase.rpc('crm_team', { p_from: from, p_to: to }).then(({ data, error }) => {
      if (error) setErr(error.message); else setRows((data as any[]) ?? []);
      setLoading(false);
    });
  }, [from, to]);
  if (loading) return <Loading />;
  if (err) return <ErrBox>{err}</ErrBox>;
  return (
    <TableBox minWidth={1100}>
      <thead><tr style={{ background: theme.bgSoft }}>
        <Th>Vendedor</Th><Th style={{ textAlign: 'right' }}>Prospectos</Th><Th style={{ textAlign: 'right' }}>Cuentas nuevas</Th>
        <Th style={{ textAlign: 'right' }}>Cotizaciones</Th><Th style={{ textAlign: 'right' }}>Venta (sin IVA)</Th><Th style={{ textAlign: 'right' }}>Cobrado</Th>
        <Th style={{ textAlign: 'right' }}>Meta</Th><Th style={{ textAlign: 'right' }}>Comisión</Th><Th style={{ textAlign: 'right' }}>Por cobrar</Th>
      </tr></thead>
      <tbody>
        {rows.map(r => {
          const p = r.performance ?? {}; const c = r.commission ?? {};
          return (
            <tr key={r.user_id} onClick={() => onPick(r.user_id)} style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}>
              <Td><Name>{r.display_name}</Name><Sub>{p.accounts_total} cuentas</Sub></Td>
              <Td style={{ textAlign: 'right' }}>{p.leads_new}<Sub>{p.leads_converted} convertidos</Sub></Td>
              <Td style={{ textAlign: 'right' }}>{p.accounts_opened}<Sub>{p.accounts_activated} activas</Sub></Td>
              <Td style={{ textAlign: 'right' }}>{p.quotes_count}<Sub>{pct(p.quotes_won, p.quotes_sent_or_won)} conv.</Sub></Td>
              <Td style={{ textAlign: 'right' }}><Ccy mxn={p.sales_mxn} usd={p.sales_usd} /></Td>
              <Td style={{ textAlign: 'right' }}><Ccy mxn={p.collected_mxn} usd={p.collected_usd} /></Td>
              <Td style={{ textAlign: 'right' }}>{c.has_plan ? <>{n(c.attainment_pct).toFixed(0)}%<Sub>de {fmtMoney(c.target_mxn, 'MXN')}</Sub></> : <span style={{ color: theme.textDim }}>Sin plan</span>}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 600 }}>{c.has_plan ? fmtMoney(c.commission_mxn, 'MXN') : <span style={{ color: theme.textDim }}>—</span>}</Td>
              <Td style={{ textAlign: 'right', color: AMBER }}><Ccy mxn={p.receivable_mxn} usd={p.receivable_usd} /></Td>
            </tr>
          );
        })}
      </tbody>
    </TableBox>
  );
}

function pct(a: any, b: any) { return n(b) > 0 ? `${Math.round(n(a) / n(b) * 100)}%` : '—'; }

function SellerPerformance({ staffId, name, from, to }: { staffId: string; name: string; from: string; to: string }) {
  const [p, setP] = useState<any>(null);
  const [c, setC] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setP(null); setC(null); setErr(null);
    Promise.all([
      supabase.rpc('crm_performance', { p_staff: staffId, p_from: from, p_to: to }),
      supabase.rpc('crm_commission', { p_staff: staffId, p_date: to }),
    ]).then(([a, b]) => {
      if (a.error) setErr(a.error.message); else setP(a.data);
      if (b.error) setErr(b.error.message); else setC(b.data);
    });
  }, [staffId, from, to]);
  if (err) return <ErrBox>{err}</ErrBox>;
  if (!p || !c) return <Loading />;
  return (
    <div style={{ display: 'grid', gap: 28 }}>
      {name && <div style={{ fontFamily: theme.font.head, fontSize: 20, color: theme.brand }}>{name}</div>}
      <Group title="Prospección">
        <Stat label="Prospectos asignados" value={fmtNum(p.leads_new)} />
        <Stat label="Convertidos en cuenta" value={fmtNum(p.leads_converted)} sub={pct(p.leads_converted, p.leads_new)} />
        <Stat label="Descartados" value={fmtNum(p.leads_discarded)} />
        <Stat label="Primera respuesta" value={p.first_response_hours != null ? `${p.first_response_hours} h` : '—'} sub="promedio" />
      </Group>
      <Group title="Cartera">
        <Stat label="Cuentas abiertas en el mes" value={fmtNum(p.accounts_opened)} />
        <Stat label="De ésas, ya compraron" value={fmtNum(p.accounts_activated)} sub={pct(p.accounts_activated, p.accounts_opened)} />
        <Stat label="Cuentas totales" value={fmtNum(p.accounts_total)} />
        <Stat label="Actividad en el portal" value={fmtNum(p.portal_events)} sub="eventos de sus cuentas" />
      </Group>
      <Group title="Venta">
        <Stat label="Cotizaciones" value={fmtNum(p.quotes_count)} sub={<Ccy mxn={p.quotes_mxn} usd={p.quotes_usd} inline />} />
        <Stat label="Conversión de cotizaciones" value={pct(p.quotes_won, p.quotes_sent_or_won)} sub={`${p.quotes_won} de ${p.quotes_sent_or_won}`} />
        <Stat label="Pedidos" value={fmtNum(p.orders_count)} />
        <Stat label="Venta sin IVA" value={<Ccy mxn={p.sales_mxn} usd={p.sales_usd} />} />
        <Stat label="Pipeline abierto" value={<Ccy mxn={p.pipeline_mxn} usd={p.pipeline_usd} />} sub={<>ponderado <Ccy mxn={p.pipeline_weighted_mxn} usd={p.pipeline_weighted_usd} inline /></>} />
      </Group>
      <Group title="Cobranza">
        <Stat label="Cobrado en el mes" value={<Ccy mxn={p.collected_mxn} usd={p.collected_usd} />} />
        <Stat label="Base comisionable" value={fmtMoney(p.collected_base_mxn, 'MXN')} sub="cobrado sin IVA, en pesos al TC del día" />
        <Stat label="Por cobrar de su cartera" value={<Ccy mxn={p.receivable_mxn} usd={p.receivable_usd} />} warn={n(p.receivable_mxn) + n(p.receivable_usd) > 0} />
      </Group>
      <CommissionCard c={c} />
    </div>
  );
}

function CommissionCard({ c }: { c: any }) {
  if (!c?.has_plan) return <Section title="Comisión"><div style={{ fontSize: 13, color: theme.textMuted }}>Este vendedor todavía no tiene plan de comisión. El admin lo configura en ⚙️ Configuración.</div></Section>;
  const att = n(c.attainment_pct);
  const tiers = (c.tiers ?? []) as any[];
  const maxPct = Math.max(120, ...tiers.map(t => n(t.from_pct) + 20), att + 10);
  return (
    <Section title={`Comisión · ${c.period === 'quarterly' ? 'trimestre' : 'mes'} ${fmtDate(c.period_start)} – ${fmtDate(c.period_end)}`}>
      <StatStrip>
        <Stat label="Meta" value={fmtMoney(c.target_mxn, 'MXN')} />
        <Stat label="Llevas (base cobrada)" value={fmtMoney(c.collected_base_mxn, 'MXN')} sub={`${att.toFixed(1)}% de la meta`} />
        <Stat label="Escalón actual" value={`${n(c.current_rate)}%`} sub={c.tier_mode === 'whole' ? 'aplica a todo el periodo' : 'por tramos'} />
        <Stat label="Comisión generada" value={fmtMoney(c.commission_mxn, 'MXN')} />
        <Stat label="Pagada" value={c.paid_mxn != null ? fmtMoney(c.paid_mxn, 'MXN') : '—'} sub={c.paid_at ? fmtDate(c.paid_at) : 'pendiente'} />
      </StatStrip>
      <div style={{ position: 'relative', height: 8, background: theme.border, marginTop: 24, marginBottom: 6 }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(att / maxPct * 100, 100)}%`, background: theme.accent }} />
        {tiers.map((t, i) => (
          <div key={i} style={{ position: 'absolute', left: `${n(t.from_pct) / maxPct * 100}%`, top: -4, bottom: -4, borderLeft: `1px solid ${theme.brand}` }}>
            <span style={{ position: 'absolute', top: 16, left: 3, fontSize: 11, color: theme.textMuted, whiteSpace: 'nowrap' }}>{n(t.from_pct)}% → {n(t.rate)}%</span>
          </div>
        ))}
      </div>
      <div style={{ height: 22 }} />
      {c.next_tier_pct != null && <div style={{ fontSize: 13, color: theme.text, marginBottom: 18 }}>Te faltan <b>{fmtMoney(c.missing_to_next_mxn, 'MXN')}</b> cobrados (sin IVA) para el escalón de <b>{n(c.next_tier_rate)}%</b> ({n(c.next_tier_pct)}% de la meta).</div>}
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, margin: '8px 0 4px' }}>Cobros que cuentan en el periodo</div>
      {(c.payments ?? []).length === 0 && <div style={{ fontSize: 12, color: theme.textDim }}>Sin cobros registrados en el periodo.</div>}
      {(c.payments ?? []).map((x: any) => (
        <Row key={x.id}>
          <span style={{ width: 100, color: theme.textMuted }}>{fmtDate(x.paid_at)}</span>
          <span style={{ flex: 1 }}>{x.customer} <span style={{ fontFamily: theme.font.mono, color: theme.textMuted, fontSize: 12 }}>{x.order_number}</span></span>
          <span style={{ color: theme.textMuted }}>{fmtMoney(x.amount, x.currency)}{x.currency === 'USD' && x.fx_rate ? ` × ${n(x.fx_rate).toFixed(2)}` : ''}</span>
          <span style={{ width: 130, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(x.base_mxn, 'MXN')}</span>
        </Row>
      ))}
    </Section>
  );
}

// =====================================================================
// CONFIGURACIÓN (admin)
// =====================================================================
function Configuracion({ sellers }: { sellers: StaffOpt[] }) {
  const [s, setS] = useState<any>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { supabase.from('crm_settings').select('*').eq('id', 1).maybeSingle().then(({ data }) => setS(data ?? {})); }, []);
  async function save() {
    setErr(null); setMsg(null);
    const { error } = await supabase.from('crm_settings').update({
      default_owner_web: s.default_owner_web || null, default_owner_whatsapp: s.default_owner_whatsapp || null,
      lead_sla_hours: Number(s.lead_sla_hours) || 24, quote_followup_days: Number(s.quote_followup_days) || 5,
      project_credit_mode: s.project_credit_mode, updated_at: new Date().toISOString(),
    }).eq('id', 1);
    if (error) setErr(error.message); else setMsg('Guardado.');
  }
  if (!s) return <Loading />;
  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <Section title="Reglas de prospectos y cuentas">
        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>Toda cuenta es del vendedor que la abrió. Aquí defines a quién llegan los prospectos que entran solos.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12 }}>
          <Field label="Prospectos de la página van a">
            <select value={s.default_owner_web ?? ''} onChange={e => setS({ ...s, default_owner_web: e.target.value })} style={inp}>
              <option value="">Sin asignar (los toma quien llegue primero)</option>
              {sellers.map(x => <option key={x.user_id} value={x.user_id}>{x.display_name}</option>)}
            </select>
          </Field>
          <Field label="Prospectos de WhatsApp van a">
            <select value={s.default_owner_whatsapp ?? ''} onChange={e => setS({ ...s, default_owner_whatsapp: e.target.value })} style={inp}>
              <option value="">Sin asignar (los toma quien llegue primero)</option>
              {sellers.map(x => <option key={x.user_id} value={x.user_id}>{x.display_name}</option>)}
            </select>
          </Field>
          <Field label="Proyecto con cuenta de otro vendedor">
            <select value={s.project_credit_mode} onChange={e => setS({ ...s, project_credit_mode: e.target.value })} style={inp}>
              <option value="owner">La venta es del dueño de la cuenta</option>
              <option value="originator">La venta es de quien trajo el proyecto</option>
            </select>
          </Field>
          <Field label="Horas para contactar un prospecto"><input type="number" value={s.lead_sla_hours} onChange={e => setS({ ...s, lead_sla_hours: e.target.value })} style={inp} /></Field>
          <Field label="Días para dar seguimiento a una cotización"><input type="number" value={s.quote_followup_days} onChange={e => setS({ ...s, quote_followup_days: e.target.value })} style={inp} /></Field>
        </div>
        {err && <ErrBox>{err}</ErrBox>}
        <Actions>{msg && <span style={{ fontSize: 12, color: '#15803D' }}>{msg}</span>}<button onClick={save} style={btnPrimary}>Guardar reglas</button></Actions>
      </Section>
      {sellers.map(x => <PlanEditor key={x.user_id} seller={x} />)}
    </div>
  );
}

function PlanEditor({ seller }: { seller: StaffOpt }) {
  const [p, setP] = useState<any>(null);
  const [exists, setExists] = useState(false);
  const [tiers, setTiers] = useState<{ from_pct: string; rate: string }[]>([]);
  const [ovr, setOvr] = useState({ ym: todayIso().slice(0, 7), target: '' });
  const [overrides, setOverrides] = useState<any[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  async function load() {
    const [{ data }, { data: o }] = await Promise.all([
      supabase.from('sales_commission_plans').select('*').eq('staff_user_id', seller.user_id).maybeSingle(),
      supabase.from('sales_targets').select('*').eq('staff_user_id', seller.user_id).order('period_start', { ascending: false }).limit(12),
    ]);
    const plan = (data as any) ?? { period: 'monthly', target_mxn: 0, tier_mode: 'whole', tiers: [{ from_pct: 0, rate: 2 }, { from_pct: 80, rate: 3 }, { from_pct: 100, rate: 4 }], active: true };
    setExists(!!data); setP(plan);
    setTiers(((plan.tiers ?? []) as any[]).map(t => ({ from_pct: String(t.from_pct), rate: String(t.rate) })));
    setOverrides((o as any[]) ?? []);
  }
  useEffect(() => { load(); }, [seller.user_id]);
  async function save() {
    setErr(null); setMsg(null);
    const clean = tiers.filter(t => t.from_pct !== '' && t.rate !== '').map(t => ({ from_pct: Number(t.from_pct), rate: Number(t.rate) })).sort((a, b) => a.from_pct - b.from_pct);
    if (clean.length === 0 || clean[0].from_pct !== 0) { setErr('El primer escalón debe empezar en 0% de la meta.'); return; }
    const row = { staff_user_id: seller.user_id, period: p.period, target_mxn: Number(p.target_mxn) || 0, tier_mode: p.tier_mode, tiers: clean, active: !!p.active, notes: p.notes ?? null, updated_at: new Date().toISOString() };
    const { error } = await supabase.from('sales_commission_plans').upsert(row);
    if (error) { setErr(error.message); return; }
    setMsg('Plan guardado.'); load();
  }
  async function saveOverride() {
    if (!ovr.target) return;
    const start = p.period === 'quarterly'
      ? (() => { const [y, m] = ovr.ym.split('-').map(Number); return `${y}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, '0')}-01`; })()
      : `${ovr.ym}-01`;
    const { error } = await supabase.from('sales_targets').upsert({ staff_user_id: seller.user_id, period_start: start, target_mxn: Number(ovr.target) });
    if (error) { setErr(error.message); return; }
    setOvr(o => ({ ...o, target: '' })); load();
  }
  async function markPaid() {
    const date = prompt('¿De qué mes/periodo pagas la comisión? (AAAA-MM)', todayIso().slice(0, 7));
    if (!date) return;
    const { data, error } = await supabase.rpc('crm_commission', { p_staff: seller.user_id, p_date: `${date}-15` });
    if (error) { setErr(error.message); return; }
    const c = data as any;
    if (!c?.has_plan) { setErr('Sin plan activo.'); return; }
    const amt = prompt(`Comisión calculada del periodo ${c.period_start}: $${fmtNum(c.commission_mxn, 2)} MXN. Monto pagado:`, String(c.commission_mxn));
    if (amt === null) return;
    const { error: e2 } = await supabase.from('sales_commission_payouts').upsert({ staff_user_id: seller.user_id, period_start: c.period_start, amount_mxn: Number(amt), paid_at: todayIso() }, { onConflict: 'staff_user_id,period_start' });
    if (e2) { setErr(e2.message); return; }
    setMsg(`Comisión de ${c.period_start} marcada como pagada.`);
  }
  if (!p) return <Loading />;
  return (
    <Section title={`Plan de comisión · ${seller.display_name}${exists ? '' : ' (sin plan todavía)'}`}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12 }}>
        <Field label="Periodo">
          <select value={p.period} onChange={e => setP({ ...p, period: e.target.value })} style={inp}><option value="monthly">Mensual</option><option value="quarterly">Trimestral</option></select>
        </Field>
        <Field label="Meta por periodo (MXN, cobrado sin IVA)"><input type="number" value={p.target_mxn} onChange={e => setP({ ...p, target_mxn: e.target.value })} style={inp} /></Field>
        <Field label="Al subir de escalón">
          <select value={p.tier_mode} onChange={e => setP({ ...p, tier_mode: e.target.value })} style={inp}>
            <option value="whole">El % aplica a todo el periodo</option>
            <option value="marginal">El % aplica solo a lo que pasa del escalón</option>
          </select>
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 18 }}><input type="checkbox" checked={!!p.active} onChange={e => setP({ ...p, active: e.target.checked })} /> Plan activo</label>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, margin: '18px 0 8px' }}>Escalones</div>
      {tiers.map((t, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, fontSize: 13 }}>
          Desde <input type="number" value={t.from_pct} onChange={e => setTiers(ts => ts.map((x, j) => j === i ? { ...x, from_pct: e.target.value } : x))} style={{ ...inp, width: 90 }} /> % de la meta →
          <input type="number" step="0.1" value={t.rate} onChange={e => setTiers(ts => ts.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} style={{ ...inp, width: 90 }} /> % de comisión
          <button onClick={() => setTiers(ts => ts.filter((_, j) => j !== i))} style={{ ...btnLink, color: RED }}>Quitar</button>
        </div>
      ))}
      <button onClick={() => setTiers(ts => [...ts, { from_pct: '', rate: '' }])} style={btnLink}>Agregar escalón</button>
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, margin: '18px 0 8px' }}>Meta distinta para un periodo específico</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="month" value={ovr.ym} onChange={e => setOvr(o => ({ ...o, ym: e.target.value }))} style={{ ...inp, width: 170 }} />
        <input type="number" placeholder="Meta MXN" value={ovr.target} onChange={e => setOvr(o => ({ ...o, target: e.target.value }))} style={{ ...inp, width: 160 }} />
        <button onClick={saveOverride} style={btnGhost}>Guardar meta del periodo</button>
        {overrides.map(o => <Tag key={o.period_start}>{o.period_start}: {fmtMoney(o.target_mxn, 'MXN')}</Tag>)}
      </div>
      {err && <ErrBox>{err}</ErrBox>}
      <Actions>
        {msg && <span style={{ fontSize: 12, color: '#15803D' }}>{msg}</span>}
        {exists && <button onClick={markPaid} style={btnGhost}>Marcar comisión pagada…</button>}
        <button onClick={save} style={btnPrimary}>Guardar plan</button>
      </Actions>
    </Section>
  );
}

// =====================================================================
// UI compartida — sobria: sin emojis, sin cajas de colores, mayúsculas
// solo donde aportan. Color solo para lo que pide atención (rojo/ámbar).
// =====================================================================
const RED = '#B91C1C';
const AMBER = '#B45309';

function Composer({ onSave }: { onSave: (kind: string, body: string) => Promise<void> }) {
  const [kind, setKind] = useState('note');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!body.trim()) return;
    setBusy(true); await onSave(kind, body.trim()); setBusy(false); setBody('');
  }
  return (
    <Section title="Registrar actividad">
      <Pills style={{ marginBottom: 8 }}>
        {['note', 'call', 'whatsapp', 'meeting', 'visit', 'email'].map(k => (
          <button key={k} onClick={() => setKind(k)} style={pill(kind === k)}>{ACT_KIND[k]}</button>
        ))}
      </Pills>
      <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="¿Qué pasó? ¿Qué se acordó?" style={{ ...inp, minHeight: 72, resize: 'vertical' }} />
      {body.trim() && <Actions><button onClick={go} disabled={busy} style={btnPrimary}>{busy ? 'Guardando…' : 'Guardar'}</button></Actions>}
    </Section>
  );
}
function ActRow({ kind, body, at, who }: { kind: string; body: string | null; at: string; who?: string | null }) {
  return (
    <div style={{ padding: '9px 0', borderTop: `1px solid ${theme.border}`, fontSize: 13 }}>
      <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 1 }}>{ACT_KIND[kind] ?? kind} · {fmtDateTime(at)}{who ? ` · ${who}` : ''}</div>
      {body && <div style={{ color: theme.text, whiteSpace: 'pre-wrap' }}>{body}</div>}
    </div>
  );
}
function Ccy({ mxn, usd, inline }: { mxn: any; usd: any; inline?: boolean }) {
  const parts: string[] = [];
  if (n(mxn) !== 0) parts.push(fmtMoney(mxn, 'MXN'));
  if (n(usd) !== 0) parts.push(fmtMoney(usd, 'USD'));
  if (parts.length === 0) return <span style={{ color: theme.textDim }}>—</span>;
  if (inline) return <span>{parts.join(' + ')}</span>;
  return <span>{parts.map((p, i) => <div key={i}>{p}</div>)}</span>;
}
function StatStrip({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', background: theme.bg, border: `1px solid ${theme.border}` }}>{children}</div>;
}
function Stat({ label, value, sub, warn }: { label: string; value: React.ReactNode; sub?: React.ReactNode; warn?: boolean }) {
  return (
    <div style={{ flex: '1 1 150px', padding: '14px 18px', borderRight: `1px solid ${theme.border}`, marginRight: -1 }}>
      <div style={{ fontSize: 12, color: theme.textMuted }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 600, color: warn ? AMBER : theme.brand, marginTop: 4, lineHeight: 1.35 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: theme.textDim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><GroupTitle>{title}</GroupTitle><StatStrip>{children}</StatStrip></div>;
}
function GroupTitle({ children, color, count }: { children: React.ReactNode; color?: string; count?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: theme.brand, marginBottom: 10 }}>
      {color && <Dot color={color} />}{children}{count != null && <span style={{ color: theme.textDim, fontWeight: 400 }}>{count}</span>}
    </div>
  );
}
function Card({ children }: { children: React.ReactNode }) {
  return <div style={{ background: theme.bg, border: `1px solid ${theme.border}` }}>{children}</div>;
}
function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.brand }}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '9px 0', borderTop: `1px solid ${theme.border}`, fontSize: 13 }}>{children}</div>;
}
function Info({ k, v }: { k: string; v: React.ReactNode }) {
  if (v == null || v === '') return null;
  return <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '5px 0', alignItems: 'baseline' }}><span style={{ width: 130, color: theme.textMuted, flexShrink: 0 }}>{k}</span><span style={{ color: theme.text, whiteSpace: 'pre-wrap' }}>{v}</span></div>;
}
function MiniRow({ a, b, mono }: { a: string; b: string; mono?: string }) {
  return (
    <Row>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a}{mono && mono !== a && <span style={{ fontFamily: theme.font.mono, color: theme.textDim, fontSize: 11 }}> {mono}</span>}</span>
      <span style={{ whiteSpace: 'nowrap', color: theme.textMuted }}>{b}</span>
    </Row>
  );
}
function Dot({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 99, background: color, flexShrink: 0 }} />;
}
function HealthChip({ h }: { h: string }) {
  const x = HEALTH[h] ?? { label: h, bg: theme.bgSoft, fg: theme.brand };
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><Dot color={x.fg} />{x.label}</span>;
}
function StageChip({ code }: { code: string }) {
  const s = LEAD_STAGES.find(x => x.code === code);
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><Dot color={s?.color ?? theme.brand} />{s?.label ?? code}</span>;
}
function NextStep({ text, at }: { text: string | null | undefined; at: string | null | undefined }) {
  if (!text) return <span style={{ color: theme.textDim }}>—</span>;
  const late = !!at && new Date(at) < new Date();
  return <span style={{ fontSize: 13 }}>{text}{at && <div style={{ fontSize: 12, color: late ? RED : theme.textDim }}>{late ? 'Vencido · ' : ''}{fmtDateTime(at)}</div>}</span>;
}
function Name({ children }: { children: React.ReactNode }) {
  return <div style={{ color: theme.brand, fontWeight: 600 }}>{children}</div>;
}
function Sub({ children, color }: { children: React.ReactNode; color?: string }) {
  return <div style={{ fontSize: 12, color: color ?? theme.textDim, marginTop: 2 }}>{children}</div>;
}
function TableBox({ children, minWidth }: { children: React.ReactNode; minWidth?: number }) {
  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: minWidth ?? 900, color: theme.text }}>{children}</table>
    </div>
  );
}
function Modal({ children, onClose, wide }: { children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,30,33,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', background: theme.bg, width: '100%', maxWidth: wide ? 1180 : 620, padding: '32px 36px', boxShadow: '0 12px 40px rgba(0,0,0,0.12)' }}>
        <button onClick={onClose} aria-label="Cerrar" style={{ position: 'absolute', top: 14, right: 16, background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: theme.textDim }}>×</button>
        {children}
      </div>
    </div>
  );
}
function ModalTitle({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div style={{ marginBottom: 6, paddingRight: 30 }}>
      <div style={{ fontSize: 12, color: theme.textMuted }}>{eyebrow}</div>
      <h2 style={{ fontFamily: theme.font.head, fontSize: 24, fontWeight: 400, color: theme.brand, margin: '2px 0 0' }}>{title}</h2>
    </div>
  );
}
function Grid2({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>{children}</div>;
}
function Actions({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 14 }}>{children}</div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block' }}><div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 5 }}>{label}</div>{children}</label>;
}
function Toolbar({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>{children}</div>;
}
function Pills({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, ...style }}>{children}</div>;
}
function pill(active: boolean): React.CSSProperties {
  return {
    padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: theme.font.sans, fontSize: 13, whiteSpace: 'nowrap',
    border: `1px solid ${active ? theme.brand : theme.border}`, background: active ? theme.brand : theme.bg,
    color: active ? theme.textOnBrand : theme.text, fontWeight: active ? 600 : 400,
  };
}
// Filtro con conteo (mismo API que antes: label/valor/color/activo)
function Kpi({ label, value, color, active, onClick }: { label: string; value: number; color: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ ...pill(active), display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      {!active && color !== theme.brand && <Dot color={color} />}
      {label}<span style={{ opacity: active ? 0.75 : 1, color: active ? theme.textOnBrand : theme.textDim }}>{value}</span>
    </button>
  );
}
function Th({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: theme.textMuted, fontWeight: 500, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap', ...style }}>{children}</th>;
}
function Td({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '12px 14px', verticalAlign: 'top', borderTop: `1px solid ${theme.border}`, ...style }}>{children}</td>;
}
function Tag({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 12, padding: '2px 8px', background: theme.bgSoft, color: theme.textMuted, whiteSpace: 'nowrap' }}>{children}</span>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>{children}</div>;
}
function Loading() { return <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div>; }
function ErrBox({ children }: { children: React.ReactNode }) {
  return <div style={{ color: RED, fontSize: 13, marginTop: 12, padding: '10px 12px', borderLeft: `3px solid ${RED}`, background: 'rgba(185,28,28,0.05)' }}>{children}</div>;
}
const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${theme.borderStrong}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font.sans };
const btnPrimary: React.CSSProperties = { background: theme.brand, color: theme.textOnBrand, border: 'none', padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans, whiteSpace: 'nowrap' };
const btnGhost: React.CSSProperties = { background: theme.bg, color: theme.brand, border: `1px solid ${theme.borderStrong}`, padding: '7px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: theme.font.sans, whiteSpace: 'nowrap' };
const btnLink: React.CSSProperties = { background: 'transparent', color: theme.brand, border: 'none', padding: 0, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans, textDecoration: 'underline', textUnderlineOffset: 3, whiteSpace: 'nowrap' };
